import React from "react";
import { useDispatch } from "react-redux";
import S3Upload from "react-s3-uploader/s3upload";

import AuthService from "src/services/auth.service";
import FileUploadService from "src/services/file.upload.service";
import { cancelNewVersionUpload, initiateUploadNewVersion, invalidateAssetUpload, uploadAsset, uploadAssetFailed, uploadAssetProgress, uploadingAssetSucceeded, uploadNewVersion } from "src/redux/actions/assets";

import { nanoid } from "@reduxjs/toolkit";

import _ from "lodash";
import cx from "classnames";
import styles from "./fileUpload.module.scss";
import { FileService } from "src/services/file.service";
import { Asset, AssetFile } from "src/types/assets";
import { useSelector } from "react-redux";
import { Line } from "rc-progress";
import Text from "src/components/text";


// eslint-disable-next-line no-undef
const apiUrl = `${process.env.REACT_APP_API_URL}`;
const API_BASE_URL = `${apiUrl}/api/2.0`;

interface FileUploadProps {
  asset: Asset;
}

const FileUpload: React.FC<FileUploadProps> = ({ asset }) => {
  const dispatch = useDispatch();

  const handleFileUploadProgress = (progress, status, file) => {
    dispatch(uploadAssetProgress({
      id: file.id,
      progress: progress
    }));
  }

  const handleFileUploadError = (file, error) => {
    dispatch(uploadAssetFailed({
      id: file.id,
      error: error
    }));
  }

  const handleFileUploadFinish = (data, file) => {

    FileUploadService.markFinish(file.id);

    dispatch(uploadingAssetSucceeded({
      id: file.id
    }));

    dispatch(uploadNewVersion({
      id: asset.id,
      signedUrl: data.signedUrl
    }));
  }


  const handleUploadNewVersion = (e) => {
    let file = e.target.files[0];
    file.id = nanoid();

    const fileName = _.get(file, "name", "Untitled");
    let fileType = FileService.getFileExtension(fileName);
    if (file.type == "" || file.type == null || file.type == undefined) {
      fileType = "application/octet-stream";
      file = new File([file], fileName, { type: fileType })
      file.id = nanoid();
    }

    dispatch(initiateUploadNewVersion({
      id: asset.id,
      file: file.id,
    }));

    const s3Uploader = new S3Upload({
      signingUrl: "/file_upload/initiate",
      signingUrlMethod: "POST",
      signingUrlHeaders: {
        Authorization: `Bearer ${AuthService.getAccessToken()}`,
      },
      server: API_BASE_URL,
      contentDisposition: "auto",
      signingUrlQueryParams: { originalName: encodeURIComponent(file.name) },
      uploadRequestHeaders: {},
      onFinishS3Put: handleFileUploadFinish,
      onProgress: handleFileUploadProgress,
      onError: handleFileUploadError,
      files: [file],
      fileSizeError: false,
    });

    FileUploadService.trackUpload(s3Uploader);

    dispatch(uploadAsset({
      file: {
        id: file.id,
        name: fileName,
        type: fileType,
        size: file.size,
      } as AssetFile
    }));
  };

  return (
    <div className={styles.uploadNewVersion}>
      <i className={cx(styles.upload, "paperflite-upload")}></i>
      <span className={styles.title}>Upload new version</span>
      <input type="file" title="" onChange={handleUploadNewVersion}></input>
    </div>
  );
};

const FileUploadStatus = ({ asset }) => {
  const dispatch = useDispatch();
  const assetFile = useSelector((state: any) => {
    const file = _.get(state, `asset.uploadVersion.file`);
    return _.get(state, `asset.upload['${file}'].item`);
  });

  const progress = _.get(assetFile, "progress", 0);

  const handleCancelNewVersionUpload = () => {
    const id = _.get(assetFile, "id");
    if(id) {
      const uploader = FileUploadService.getUploader(id);
      if(uploader) {
        uploader.abortUpload();
        dispatch(invalidateAssetUpload(id));
      }
    }

    dispatch(cancelNewVersionUpload(asset!.id)); 
  }

  return (
    <div className={styles.uploadProgressContainer}>
      <div className={styles.uploadProgress}>
        <p className={styles.uploading}>uploading</p>
        <div className={styles.fileName}>
          <Text text={_.get(assetFile, "name", "")} />
        </div>
        {progress !== 100 && (
          <button className={styles.close}>
            <i className={cx("paperflite-close")} onClick={() => handleCancelNewVersionUpload()} />
          </button>
        )}
      </div>
      <div className={styles.progress}>
        <div className={styles.bar} style={{ width: `${progress}%` }}></div>
      </div>
    </div>
  );
}

export { FileUpload, FileUploadStatus };
